import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  ViewChild
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {AbstractControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatDividerModule} from '@angular/material/divider';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatSelectModule} from '@angular/material/select';
import {ResearchPaperDTO, ResearchSource} from '../../../dto/research-paper';
import {
  ScreeningSettingsResearchDBDTO,
  ScreeningSettingsResearchDBTypes,
  ScreeningSettingsTestResearchDTO
} from '../../../dto/screening-settings';
import {Store} from '@ngrx/store';
import {ScreeningSettingsService} from '../../../services/screening-settings/screening-settings.service';
import {combineLatest, distinctUntilChanged, of} from 'rxjs';
import {selectPapersByProjectIdAndQueryId} from '../../../services/research-paper/research-paper.selectors';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {ResearchKeywordDTO} from '../../../dto/research-keywords';
import {SearchKeywordGroupComponent} from '../search-keyword-group/search-keyword-group.component';
import {hasScopusAPIKey} from '../../../../../utils/models/llm-setting';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {switchMap} from 'rxjs/operators';

interface SelectOption {
  value: ScreeningSettingsResearchDBTypes;
  viewValue: string;
  infoText: string;
}

@Component({
  selector: 'app-screening-research-settings-field',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatDividerModule,
    MatProgressBarModule,
    MatSelectModule,
    SearchKeywordGroupComponent
  ],
  templateUrl: './screening-research-settings-field.component.html',
  styleUrl: './screening-research-settings-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScreeningResearchSettingsFieldComponent implements OnInit, AfterViewInit {
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly service: ScreeningSettingsService = inject(ScreeningSettingsService);
  private readonly store: Store = inject(Store);
  private readonly injector = inject(Injector);

  readonly formGroup = input.required<FormGroup>();
  readonly keywords = input.required<ResearchKeywordDTO[]>();
  readonly projectId = input.required<number>();
  readonly enabled = input<boolean>(true);
  readonly screeningSettingsId = input.required<number>();
  readonly source = input.required<ResearchSource>();
  readonly setting = input<ScreeningSettingsResearchDBDTO>();
  readonly remove = output<number | undefined>()
  readonly createdQuery = output<ScreeningSettingsResearchDBDTO>()

  @ViewChild('autosize') autosize?: CdkTextareaAutosize;

  loading: boolean = false;
  testQueryCountResult?: number;
  queryType?: ScreeningSettingsResearchDBTypes;

  readonly papers = toSignal(
    combineLatest([toObservable(this.projectId), toObservable(this.setting)]).pipe(
      switchMap(([projectId, setting]) => setting?.id
        ? this.store.select(selectPapersByProjectIdAndQueryId(projectId, setting.id))
        : of([] as ResearchPaperDTO[])
      )
    ),
    {initialValue: [] as ResearchPaperDTO[]}
  );

  researchDBTypes: SelectOption[] = [
    {
      value: ScreeningSettingsResearchDBTypes.LLM,
      viewValue: 'LLM',
      infoText: 'Query generated by the LLM based on your project settings.'
    },
    {
      value: ScreeningSettingsResearchDBTypes.GENERATED,
      viewValue: 'Generated',
      infoText: 'Query generated based on your keywords. Use the regenerate button to rebuild it after keyword changes.'
    }
  ]

  ngOnInit(): void {
    if (!!this.setting()) {
      this.formGroup().patchValue(this.setting()!);
      this.queryType = this.setting()?.queryType;
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.formGroup().get('queryType')?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.regenerationQuery();
      });
  }

  triggerResize() {
    if (!this.autosize) {
      return;
    }
    afterNextRender(
      () => {
        this.autosize!.resizeToFitContent(true);
      },
      {
        injector: this.injector,
      },
    );
  }

  get currentResearchDBType(): ScreeningSettingsResearchDBTypes {
    return this.formGroup().get('queryType')?.value;
  }


  get showRegenerateButton(): boolean {
    return this.currentResearchDBType === ScreeningSettingsResearchDBTypes.LLM
      || this.currentResearchDBType === ScreeningSettingsResearchDBTypes.GENERATED;
  }

  getScreeningSettingsResearchDBDTO() {
    return {
      ...this.setting(),
      ...this.formGroup().value,
      source: this.source(),
      screeningSettingsId: this.screeningSettingsId()
    } as ScreeningSettingsResearchDBDTO;
  }

  removeField(): void {
    const id = this.setting()?.id;
    this.remove.emit(id);
  }

  regenerationQuery(event?: any) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.currentResearchDBType) {
      return;
    }
    if (!event && this.queryType === this.currentResearchDBType) {
      return;
    }
    this.queryType = this.currentResearchDBType;
    const currentQuery = this.formGroup().get('query')?.value;

    let llmBased = this.currentResearchDBType === ScreeningSettingsResearchDBTypes.LLM;
    this.loading = true;
    this.formGroup().patchValue({query: ""});

    this.cdr.detectChanges(); //ScreeningSettingsServiceTypes
    this.service.getSimpleSetting(this.projectId(), this.source() as any, llmBased).subscribe({
      next: (setting) => {
        this.formGroup().patchValue({query: setting.query});
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.formGroup().patchValue({query: currentQuery});
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  testCountQuery(event?: any) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.testQueryCountResult = undefined;
    const currentQuery = this.formGroup().get('query')?.value;
    const query: ScreeningSettingsTestResearchDTO = {
      query: currentQuery,
      source: this.source(),
      enableYearFilter: false,
    }
    this.loading = true;
    this.cdr.detectChanges();
    this.service.testCountQuery(this.projectId(), query).subscribe({
      next: (result) => {
        this.testQueryCountResult = result;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }


  applyFlow = effect(() => {
    const enabled = this.enabled();
    const control = this.formGroup().get('query');
    const queryTypeControl = this.formGroup().get('queryType');
    if (enabled) {
      control?.setValidators([Validators.required, this.validQuery.bind(this)]);
      control?.enable();
      queryTypeControl?.enable();
    } else {
      queryTypeControl?.disable();
      control?.clearValidators();
      control?.disable();
    }
    control?.updateValueAndValidity();
    queryTypeControl?.updateValueAndValidity();
    this.cdr.detectChanges();
  });

  validQuery(control: AbstractControl) {
    const currentType = this.source();
    if (currentType === ResearchSource.PUBMED) {
      return this.validPubMedQueryValidator(control);
    }
    if (currentType === ResearchSource.SCOPUS) {
      return this.validScopusQueryValidator(control);
    }
    if (currentType === ResearchSource.SEMANTIC_SCHOLAR) {
      return this.validSemanticQueryValidator(control);
    }
    return null;
  }

  validScopusQueryValidator(control: AbstractControl) {
    const VALID_FIELD_CODES: string[] = [
      'TITLE', 'ABS', 'KEY', 'AUTH', 'AFFIL', 'SRCTITLE', 'DOCTYPE', 'LANGUAGE', 'PUBYEAR', 'TITLE-ABS-KEY'
    ];

    const query: string = control.value;
    // Check if the query is null or empty
    if (!query || query.trim().length === 0) {
      return {validQuery: true}; // Invalid
    }
    // Check for balanced parentheses
    const openParensCount = (query.match(/\(/g) || []).length;
    const closeParensCount = (query.match(/\)/g) || []).length;
    if (openParensCount !== closeParensCount) {
      return {validQuery: true}; // Invalid
    }
    // Check for unsupported characters
    if (query.includes("{") || query.includes("}")) {
      return {validQuery: true}; // Invalid
    }

    const pattern = /\b([A-Z]+)\(/g;
    const matcher = query.matchAll(pattern);
    for (const match of matcher) {
      const code = match[1];
      if (!VALID_FIELD_CODES.includes(code)) {
        return {validQuery: true}; // Invalid
      }
    }
    // Add additional checks if needed
    return null; // Valid
  }


  validSemanticQueryValidator(control: AbstractControl) {
    const query: string = control.value;
    // Check if the query is null or empty
    if (!query || query.trim().length === 0) {
      return {validQuery: true}; // Invalid
    }
    // Check for balanced parentheses
    const openParensCount = (query.match(/\(/g) || []).length;
    const closeParensCount = (query.match(/\)/g) || []).length;
    if (openParensCount !== closeParensCount) {
      return {validQuery: true}; // Invalid
    }
    // Check for unsupported characters
    if (query.includes("{") || query.includes("}")) {
      return {validQuery: true}; // Invalid
    }
    // Add additional checks if needed
    return null; // Valid
  }

  validPubMedQueryValidator(control: AbstractControl) {
    const query: string = control.value;
    // Check if the query is null or empty
    if (!query || query.trim().length === 0) {
      return {validQuery: true}; // Invalid
    }
    // Check for balanced parentheses
    const openParensCount = (query.match(/\(/g) || []).length;
    const closeParensCount = (query.match(/\)/g) || []).length;
    if (openParensCount !== closeParensCount) {
      return {validQuery: true}; // Invalid
    }
    // Check for unsupported characters
    if (query.includes("{") || query.includes("}")) {
      return {validQuery: true}; // Invalid
    }
    // Add additional checks if needed
    return null; // Valid
  }

  isGenerated() {
    return this.currentResearchDBType === ScreeningSettingsResearchDBTypes.GENERATED;
  }

  protected readonly hasScopusAPIKey = hasScopusAPIKey;
}
